import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  EmojiEvents as EventsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Redeem as RedeemIcon
} from '@mui/icons-material';
import api from '../api';

function EventRewards({ userId }) {
  const [expandedPanel, setExpandedPanel] = useState(false);

  // Fetch user data
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId
  });

  // Fetch user's event rewards
  const { data: rewardsData, isLoading, isError } = useQuery({
    queryKey: ['rewards', 'user', userId],
    queryFn: async () => {
      if (!userId) return { rewards: {}, totalCount: 0 };
      const response = await api.get(`/users/${userId}/rewards`);
      return response.data;
    },
    enabled: !!userId
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const formatRewardType = (type) => {
    if (!type) return 'Unknown';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getRewardTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'nft': return 'primary';
      case 'token': return 'success';
      case 'moani': return 'info';
      case 'item': return 'secondary';
      default: return 'default';
    }
  };

  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">No user ID provided</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load user event rewards.
      </Alert>
    );
  }

  const groupedRewards = rewardsData?.rewards || {};
  const totalRewards = rewardsData?.totalCount || 0;
  
  // Calculate summary statistics
  const allRewards = Object.values(groupedRewards).flat();
  const claimedRewards = allRewards.filter(reward => reward.claimed);
  const unclaimedRewards = allRewards.filter(reward => !reward.claimed);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <EventsIcon sx={{ mr: 2, color: 'primary.main' }} />
        Event Rewards for {userData?.name || 'User'}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RedeemIcon sx={{ color: 'primary.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="primary">
                {totalRewards}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Rewards
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="success.main">
                {claimedRewards.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Claimed Rewards
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CancelIcon sx={{ color: 'warning.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="warning.main">
                {unclaimedRewards.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unclaimed Rewards
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EventsIcon sx={{ color: 'info.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="info.main">
                {Object.keys(groupedRewards).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Event Categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Rewards by Event */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Rewards by Event ({totalRewards} total)
        </Typography>
        
        {Object.keys(groupedRewards).length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No event rewards found for this user.
            </Typography>
          </Box>
        ) : (
          <Box>
            {Object.entries(groupedRewards).map(([eventName, rewards], index) => (
              <Accordion 
                key={eventName} 
                expanded={expandedPanel === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h6">{eventName}</Typography>
                      <Chip 
                        label={`${rewards.length} reward${rewards.length !== 1 ? 's' : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box display="flex" gap={1} mr={2}>
                      <Chip 
                        label={`${rewards.filter(r => r.claimed).length} claimed`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${rewards.filter(r => !r.claimed).length} unclaimed`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Asset</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rewards.map((reward) => (
                        <TableRow key={reward.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {reward.asset?.name || reward.rewardId}
                              </Typography>
                              {reward.asset?.category && (
                                <Typography variant="caption" color="text.secondary">
                                  {reward.asset.category}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={formatRewardType(reward.rewardType)}
                              size="small"
                              color={getRewardTypeColor(reward.rewardType)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {reward.rewardAmount}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={reward.claimed ? 'Claimed' : 'Unclaimed'}
                              size="small"
                              color={reward.claimed ? 'success' : 'warning'}
                              icon={reward.claimed ? <CheckCircleIcon /> : <CancelIcon />}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(reward.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(reward.createdAt).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {reward.description || 'No description'}
                              </Typography>
                              {reward.description && (
                                <Tooltip title={reward.description}>
                                  <IconButton size="small">
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default EventRewards;

